/**
 * HR's NTD "also notify" list (Stevee, 9 Oct 2026).
 *
 * Every no-travel day is approved on submit and told to the person's supervisor, HR, and the
 * people on this list. People are chosen from the roster, never typed, and stored by EE Key.
 * Every add and remove is in the admin log. A face, no rules: the API enforces HR-only.
 */
import { mdy, type NtdNotifyEntry, type PersonRef } from "@scheduler/shared";
import { useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";

export function NtdNotifyList() {
  const api = useSchedulerApi();
  const [entries, setEntries] = useState<NtdNotifyEntry[] | null>(null);
  const [people, setPeople] = useState<PersonRef[]>([]);
  const [chosen, setChosen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    api.ntdNotifyList().then(setEntries, (e: unknown) => setError(message(e, "Could not load the list.")));
    api.people().then(setPeople, (e: unknown) => setError(message(e, "Could not load the roster.")));
  }, [api, back]);

  async function run(action: () => Promise<NtdNotifyEntry[]>) {
    setBusy(true);
    setError(null);
    try {
      setEntries(await action());
      setChosen("");
    } catch (e) {
      setError(message(e, "Could not change the list."));
    } finally {
      setBusy(false);
    }
  }

  const listed = new Set(entries?.map((e) => e.person.id) ?? []);
  const addable = people.filter((p) => !listed.has(p.id));

  return (
    <div className="card card-pad">
      <h3>No-travel day notifications</h3>
      <p className="hint">
        Every no-travel day is approved when it is booked. The person's supervisor and HR are told
        automatically. Add anyone else who should be told.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {!entries ? <p className="muted">Loading…</p> : entries.length === 0 ? (
        <p className="muted">Nobody else is on the list.</p>
      ) : (
        <table className="data">
          <thead>
            <tr><th>Person</th><th>Added</th><th /></tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.person.ajeraKey}>
                <td>
                  {e.person.name}
                  {e.active ? null : <span className="sub">Inactive, so not told</span>}
                </td>
                <td>
                  {mdy(e.addedAt.slice(0, 10))}
                  <span className="sub">by {e.addedBy.name}</span>
                </td>
                <td>
                  <button type="button" className="ghost" disabled={busy}
                    onClick={() => void run(() => api.ntdNotifyRemove(e.person.ajeraKey))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row2">
        <label className="field">
          <span className="label">Add a person</span>
          <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
            <option value="">Choose from the roster…</option>
            {addable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <div className="field">
          <span className="label" aria-hidden="true">&nbsp;</span>
          <button type="button" className="primary" disabled={!chosen || busy}
            onClick={() => void run(() => api.ntdNotifyAdd(chosen))}>
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}
